/* ============================================================
   Kalendar — paparan kanan bagi tab TikTok (/workspace-v3.9)

   Model: setiap HARI memilih templatenya sendiri. Template itu yang
   menjana kotak waktu kosong pada hari berkenaan, dan setiap kotak
   kosong ialah sasaran seretan — pengguna menarik satu produk ke
   dalamnya, kemudian memilih set mana.

   Sebab template duduk pada HARI dan bukan pada seluruh minggu:
   pelanggan mahu Isnin laju (tiap 30 minit) tetapi hujung minggu
   perlahan (tiga kali sehari). Satu template untuk semua hari memaksa
   dia memilih antara kedua-duanya.

   Kotak kosong pula bukan hiasan. Tanpa ia, hari yang belum berisi
   kelihatan seperti ruang kosong biasa — tiada apa-apa memberitahu
   pengguna bahawa di situlah produk patut dijatuhkan, atau pukul
   berapa slot itu akan disiarkan.

   ⚠️ PROTOTAIP · REKA BENTUK SAHAJA. Tiada apa-apa disimpan dan tiada
   video benar-benar dijadualkan ke TikTok.
   ============================================================ */

import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, Plus, Check, Trash2, Video } from 'lucide-react';

import { Btn } from '../design/ui';
import { useT } from '../i18n';
import PilihWaktuV39 from './PilihWaktuV39';
import PilihV39 from './PilihV39';
import { HARI_LABEL, BULAN_LABEL, slotHari, gabungWaktu, HAD_SLOT_SEHARI, cadangWaktuBaharu, kunciSlot, minitKeJam, jamKeMinit, tarikhKeDate, tarikhHariIni, tarikhTambah, bezaHariIni, adalahTarikh } from './scheduleV39';

const HARI_PENUH = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/* Nama bulan penuh (Inggeris, sepadan BULAN_LABEL) — dipakai oleh tajuk "Bulan
   Tahun" bar tarikh v4. */
const BULAN_PENUH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/* Bilangan hari yang dipapar serentak. TIGA (arahan user 2026-08-13;
   sebelum ini empat). Lajur yang lebih lebar bermakna nama produk dalam
   slot kurang terpotong — pada empat lajur ia putus di tengah nama dan
   dua produk berlainan kelihatan serupa.

   Nilai ini dibaca di DUA tempat: bilangan lajur grid dan langkah butang
   ‹ ›. Ia satu pemalar supaya butang tidak boleh melangkau atau
   mengulang hari.

   ⛔ JANGAN kembalikan tatal mendatar. Sebabnya bukan citarasa: tetikus
      tanpa tolak-tepi dan kebanyakan pad jejak tidak boleh menatal
      mendatar langsung, jadi bagi sebahagian pengguna hari yang berada
      di luar skrin memang TIDAK BOLEH dicapai. */
const HARI_SETINGKAP = 3;

/* Hari terdekat dinamakan mengikut kedudukannya berbanding hari ini,
   bukan nama harinya. "Tomorrow" menjawab soalan pengguna terus;
   "Tuesday" memaksa dia mengira sendiri. Hari yang lebih jauh memang
   perlu nama hari — pada jarak itu "3 hari lagi" pula yang menyusahkan. */
function namaHari(tarikh, d, t) {
    /* Dikira daripada jam SEBENAR pada saat ia dilukis, bukan daripada
       nombor yang disimpan — kalau tidak, "Tomorrow" akan terus berkata
       Tomorrow selepas tengah malam berlalu (lihat seksyen TARIKH SEBENAR
       dalam scheduleV39.js). */
    const beza = bezaHariIni(tarikh);
    if (beza === 0) return t('workspace.tiktok.cal.dayToday');
    if (beza === 1) return t('workspace.tiktok.cal.dayTomorrow');
    if (beza === -1) return t('workspace.tiktok.cal.dayYesterday');
    return HARI_PENUH[d.getDay()];
}

/* ---------- Butiran ------------------------------------------------ */

function ButiranModal({ isi, namaTemplate, capsyen, onClose, stSekarang = '', sbSekarang = '', mdSekarang = '', onTukarStatus, onPadamVideo }) {
    const t = useT();
    const d = tarikhKeDate(isi.hari);
    /* ★ LANGKAH 5 — status dibaca daripada ENTRI SLOT (dihantar masuk sebagai
       `stSekarang`), bukan lagi daripada produk. Ia LANGSUNG, jadi bila
       pengguna menukar status melalui dropdown di bawah, baris ini turut
       berubah serta-merta. */
    const stKey = stSekarang;
    const stSebab = sbSekarang || '';
    const stBase = stKey === 'complete' ? (mdSekarang === 'now' ? t('workspace.tiktok.cal.stPosted') : t('workspace.tiktok.cal.stDone'))
        : stKey === 'draft' ? t('workspace.tiktok.cal.stDraft')
            : stKey === 'failed' ? t('workspace.tiktok.cal.stFail')
                : stKey === 'uncertain' ? t('workspace.tiktok.cal.stUncertain')
                    : stKey === 'running' ? t('workspace.tiktok.cal.stRunning')
                        : t('workspace.tiktok.cal.statusWaiting');
    /* Sebab disertakan pada baris status supaya pengguna nampak KENAPA. */
    const stLabel = (stSebab && (stKey === 'failed' || stKey === 'draft' || stKey === 'uncertain'))
        ? `${stBase} — ${stSebab}` : stBase;
    /* Video wujud untuk set slot ini? Butang "Padam video" hanya bermakna bila
       ada video untuk dipadam. */
    const adaVideoSet = (isi.p.videos || [])[Number(isi.s.slice(3)) - 1] === 1;
    return createPortal(
        <div
            className="s-root s-modal"
            style={{ background: 'rgba(0, 0, 0, .62)' }}
            role="dialog" aria-modal="true" aria-label={t('workspace.tiktok.cal.detailAria')}
            onClick={onClose}
        >
            <div className="s-modal__panel" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
                <header className="s-modal__head">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="s-cardname" style={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {isi.p.name}
                        </p>
                        <p className="s-dim" style={{ marginTop: 1 }}>
                            {t('workspace.page.setN', { n: isi.s.slice(3) })}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} aria-label={t('workspace.tiktok.cal.close')} className="s-modal__x">
                        <X size={15} strokeWidth={2} />
                    </button>
                </header>

                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ border: '1px solid var(--s-line)', borderRadius: 'var(--s-r-lg)', overflow: 'hidden' }}>
                        {[
                            [t('workspace.tiktok.cal.rowDate'), `${HARI_PENUH[d.getDay()]}, ${d.getDate()} ${BULAN_LABEL[d.getMonth()]} ${d.getFullYear()}`],
                            [t('workspace.tiktok.cal.rowTime'), minitKeJam(isi.minit)],
                            [t('workspace.tiktok.cal.rowTemplate'), namaTemplate || '—'],
                            [t('workspace.tiktok.cal.rowStatus'), stLabel],
                        ].map(([label, nilai], i, semua) => (
                            <div key={label} style={{
                                display: 'grid', gridTemplateColumns: '84px minmax(0,1fr)',
                                gap: 10, padding: '9px 12px', fontSize: 13,
                                borderBottom: i === semua.length - 1 ? 'none' : '1px solid var(--s-line)',
                            }}>
                                <span style={{ color: 'var(--s-ink-2)' }}>{label}</span>
                                <span>{nilai}</span>
                            </div>
                        ))}
                    </div>

                    <div>
                        <p className="s-lbl">{t('workspace.tiktok.cal.caption')}</p>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--s-ink-2)', lineHeight: 1.55 }}>
                            {/* Produk itu sendiri dihantar sebagai hujah KETIGA. Prototaip
                                mengarang caption daripada nama sahaja dan mengabaikannya,
                                tetapi workspace sebenar perlu membaca caption yang memang
                                sudah tersimpan pada produk — dan mencarinya semula ikut
                                NAMA akan tersilap sebaik dua produk berkongsi nama yang
                                sama (biasa selepas Duplicate). */}
                            {capsyen(isi.p.name, Number(isi.s.slice(3)), isi.p)}
                        </p>
                    </div>

                    {/* ★ LANGKAH 5 — kawalan per-slot: tukar status manual +
                        padam video. Duduk di dalam popup (bukan atas setiap
                        kotak) supaya kotak kalendar kekal kemas. */}
                    {(onTukarStatus || (onPadamVideo && adaVideoSet)) && (
                        <div style={{
                            display: 'flex', flexDirection: 'column', gap: 12,
                            borderTop: '1px solid var(--s-line)', paddingTop: 12,
                        }}>
                            {onTukarStatus && (
                                <div>
                                    <p className="s-lbl">{t('workspace.tiktok.cal.editStatus')}</p>
                                    <PilihV39
                                        ariaLabel={t('workspace.tiktok.cal.editStatus')}
                                        nilai={stKey === 'complete' && mdSekarang === 'now' ? 'posted' : (stKey || '')}
                                        onPilih={(v) => onTukarStatus(v)}
                                        pilihan={[
                                            { id: '', label: t('workspace.tiktok.cal.stNone') },
                                            { id: 'complete', label: t('workspace.tiktok.cal.stDone') },
                                            /* ★ [2026-09-29] 'posted' = complete + md 'now' — ditukar dalam schedSetStatusSlot. */
                                            { id: 'posted', label: t('workspace.tiktok.cal.stPosted') },
                                            { id: 'draft', label: t('workspace.tiktok.cal.stDraft') },
                                            { id: 'failed', label: t('workspace.tiktok.cal.stFail') },
                                        ]}
                                        tinggi={32}
                                        saizTeks={13}
                                        lebarMenu={240}
                                    />
                                </div>
                            )}
                            {onPadamVideo && adaVideoSet && (
                                <button
                                    type="button"
                                    onClick={() => { onPadamVideo(); onClose(); }}
                                    title={t('workspace.tiktok.cal.delVideoTip')}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        alignSelf: 'flex-start', height: 32, padding: '0 12px',
                                        borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                                        fontSize: 12.5, fontWeight: 600, color: 'var(--s-stop)',
                                        background: 'transparent', border: '1px solid var(--s-stop)',
                                    }}
                                >
                                    <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                                    {t('workspace.tiktok.cal.delVideo')}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <footer className="s-modal__foot">
                    <span style={{ flex: 1 }} />
                    <Btn size="sm" onClick={onClose}>{t('workspace.tiktok.cal.close')}</Btn>
                </footer>
            </div>
        </div>,
        document.body,
    );
}

/* ---------- Satu kotak waktu --------------------------------------- */

/* Muatan seretan yang datang DARIPADA slot lain, bukan daripada senarai
   produk. Awalan ini yang membezakan "pindahkan yang sudah ada" daripada
   "letakkan yang baharu" — id produk tidak pernah bermula dengan `slot:`. */
const AWALAN_PINDAH = 'slot:';

/* Beberapa slot sekali gus. Awalan berasingan daripada `slot:` supaya
   penerima tidak perlu meneka sama ada muatan itu satu atau banyak. */
const AWALAN_PINDAH_BANYAK = 'slots:';

/* Seret SATU set video tertentu daripada kad senarai (arahan user 2026-08-21).
   Muatan: `setvid:${pid}|${setKey}`. Beza dengan seret produk biasa (yang
   memilih set automatik) — di sini pengguna memilih set itu sendiri. */
const AWALAN_SET = 'setvid:';

function Slot({
    hari, minit, isi, sasar, setSasar, onLetak, onLetakSet, onPindah, onPindahBanyak,
    onTukarSet, onBuang, onButiran, luar = {},
    dipilih = false, pilihanSemua = null, onToggle,
    waktuSendiri = false, onBuangWaktu,
    /* Langkah 4: pengendali Retry untuk slot GAGAL. Kini belum diwayar
       (Langkah 4), jadi butang Retry tidak muncul. Status slot sendiri
       dibaca terus daripada produk — lihat `statusSlot` di bawah. */
    onRetryGagal,
}) {
    const t = useT();
    const kunci = kunciSlot(hari, minit);

    /* Satu pintu untuk kedua-dua jenis jatuhan. Slot terisi turut menerima
       jatuhan sekarang (dahulu hanya slot kosong): menjatuhkan sesuatu di
       atas slot berisi bermakna DUA-DUA bertukar tempat, bukan yang lama
       ditimpa. Menimpa akan memadamkan kerja pengguna tanpa amaran. */
    const terimaJatuhan = (e) => {
        e.preventDefault();
        setSasar(null);
        const muatan = e.dataTransfer.getData('text/plain');
        if (!muatan) return;
        if (muatan.startsWith(AWALAN_PINDAH_BANYAK)) {
            onPindahBanyak?.(muatan.slice(AWALAN_PINDAH_BANYAK.length).split(',').filter(Boolean), hari, minit);
        } else if (muatan.startsWith(AWALAN_PINDAH)) {
            onPindah?.(muatan.slice(AWALAN_PINDAH.length), hari, minit);
        } else if (muatan.startsWith(AWALAN_SET)) {
            const [pid, setKey] = muatan.slice(AWALAN_SET.length).split('|');
            if (pid && setKey) onLetakSet?.(pid, setKey, hari, minit);
        } else {
            onLetak(muatan, hari, minit);
        }
    };

    const kesanSeret = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (sasar !== kunci) setSasar(kunci);
    };

    const lepasSeret = () => setSasar((v) => (v === kunci ? null : v));

    if (!isi) {
        return (
            <div
                className={'s-slot' + (sasar === kunci ? ' s-slot--sasar' : '')}
                onDragOver={kesanSeret}
                onDragLeave={lepasSeret}
                onDrop={terimaJatuhan}
            >
                <span className="s-slot__jam">
                    {minitKeJam(minit)}
                    {waktuSendiri && <span className="s-slot__tandasendiri" title={t('workspace.tiktok.cal.extraTimeDot')}>•</span>}
                </span>
                <span className="s-slot__ajak">
                    <Plus size={12} strokeWidth={2} aria-hidden="true" />
                    {t('workspace.tiktok.cal.dropProduct')}
                </span>
                {/* Hanya waktu tambahan yang boleh dibuang, dan hanya semasa ia
                    KOSONG — membuang waktu yang berisi bermakna video di dalamnya
                    hilang serentak tanpa pengguna memilih untuk membuangnya. */}
                {waktuSendiri && onBuangWaktu && (
                    <button
                        type="button" className="s-slot__buangwaktu"
                        title={t('workspace.tiktok.cal.removeTime')} aria-label={t('workspace.tiktok.cal.removeTime')}
                        onClick={(e) => { e.stopPropagation(); onBuangWaktu(hari, minit); }}
                    >
                        <X size={10} strokeWidth={2.4} />
                    </button>
                )}
            </div>
        );
    }

    /* Slot boleh diisi produk yang videonya BELUM wujud (keputusan user
       2026-08-13) — produk ditanda Schedule sebelum Flow dijalankan. Slot
       begini menempah waktu dan menunggu; ia mesti berkata begitu dengan
       terang, kalau tidak ia kelihatan sama seperti slot yang sudah sedia
       dihantar. */
    const menungguVideo = (isi.p.videos || [])[Number(isi.s.slice(3)) - 1] !== 1;

    /* ★ LANGKAH 5 — status SEBENAR post TikTok untuk slot ini, dibaca daripada
       ENTRI SLOT sendiri (`isi.st`), BUKAN daripada produk. Ini yang membuat ia
       KEKAL: slot ingat hasilnya sendiri, jadi menjalankan produk sama di slot
       lain (atau menekan Stop) tidak lagi memadam tandanya.
       'complete' → hijau · 'failed' → merah · 'uncertain' → kuning ·
       'running' → biru · tiada → biasa (belum dijalankan). */
    const statusSlot = isi.st || null;
    const statusGagal = statusSlot === 'failed';
    /* Sebab status (kenapa gagal / draf) — dipapar di bawah label. */
    const statusSebab = isi.sb || '';

    /* ★ Gambar kecil video set yang DIPILIH (isi.s) — dipapar di kanan sekali.
       Hanya URL imej sebenar (http/https) dipapar; video local:// tanpa poster
       awan atau slot yang belum bervideo jatuh ke petak placeholder. */
    const gambarSet = isi.p.prompts?.[isi.s]?.uploadedVideoThumb;
    const gambarSah = typeof gambarSet === 'string' && /^https?:\/\//.test(gambarSet);

    return (
        <div
            data-kunci-slot={kunci}
            className={
                's-slot s-slot--isi'
                + (menungguVideo ? ' s-slot--tunggu' : '')
                + (statusGagal ? ' s-slot--gagal' : '')
                + (statusSlot === 'complete' ? ' s-slot--siap' : '')
                + (statusSlot === 'draft' ? ' s-slot--draf' : '')
                + (statusSlot === 'uncertain' ? ' s-slot--takpasti' : '')
                + (statusSlot === 'running' ? ' s-slot--jalan' : '')
                + (sasar === kunci ? ' s-slot--sasar' : '')
                + (dipilih ? ' s-slot--pilih' : '')
            }
            /* Klik pada badan slot menanda/menyahtanda. Butang di dalamnya
               (×, S1/S2/S3, nama) dikecualikan — kalau tidak, menekan S2
               akan turut menanda slot itu dan pengguna tidak akan faham
               kenapa. Shift+klik menanda satu JULAT, kerana kes sebenar di
               sini ialah "ambil 08:00 sampai 12:00", bukan memetik satu-satu. */
            onClick={(e) => {
                if (e.target.closest('button')) return;
                onToggle?.(kunci, e.shiftKey);
            }}
            /* Seluruh kotak boleh diseret — bukan pemegang berasingan. Kotak ini
               kecil, dan pemegang kecil di dalam kotak kecil lebih sukar dikena
               daripada kotak itu sendiri. Butang di dalamnya (×, S1/S2/S3, nama)
               tetap boleh diklik seperti biasa; seretan hanya bermula apabila
               tetikus benar-benar bergerak sambil ditekan. */
            draggable
            /* Menyeret slot yang DITANDA membawa semua slot bertanda sekali.
               Slot yang tidak ditanda kekal berseorangan, jadi seretan tunggal
               tidak pernah bertukar menjadi borong tanpa disedari. */
            onDragStart={(e) => {
                const pukal = dipilih && pilihanSemua && pilihanSemua.size > 1;
                e.dataTransfer.setData(
                    'text/plain',
                    pukal ? AWALAN_PINDAH_BANYAK + [...pilihanSemua].join(',') : AWALAN_PINDAH + kunci,
                );
                e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={kesanSeret}
            onDragLeave={lepasSeret}
            onDrop={terimaJatuhan}
            title={dipilih && pilihanSemua && pilihanSemua.size > 1
                ? t('workspace.tiktok.cal.dragMarked', { n: pilihanSemua.size })
                : t('workspace.tiktok.cal.slotHint')}
        >
            {/* Kiri: masa, nama, pemilih set, status. Kanan: gambar kecil video
                set dipilih. Susunan ini (maklumat kiri · gambar kanan) arahan
                user 2026-08-20. */}
            <div className="s-slot__kiri">
            <span className="s-slot__atas">
                <span className="s-slot__jam">{minitKeJam(minit)}</span>
                <button
                    type="button" className="s-slot__buang"
                    aria-label={t('workspace.tiktok.cal.removeProductAria')} title={t('workspace.tiktok.cal.removeProduct')}
                    onClick={() => onBuang(hari, minit)}
                >
                    <X size={11} strokeWidth={2.4} />
                </button>
            </span>

            <button
                type="button"
                className="s-slot__nama"
                /* Bila slot DIPILIH, klik nama = NYAHPILIH (bukan buka popup) —
                   arahan user 2026-08-20: pengguna cuba nyahpilih dengan klik kad
                   tetapi tersilap tekan nama lalu popup terbuka. Kini seluruh kad
                   (termasuk nama) menyahpilih semasa dipilih. Bila TIDAK dipilih,
                   nama buka popup butiran seperti biasa. */
                onClick={(e) => {
                    if (dipilih && onToggle) { onToggle(kunci, e.shiftKey); return; }
                    onButiran({ ...isi, hari, minit });
                }}
            >
                {isi.p.name}
            </button>

            {menungguVideo && (
                <span className="s-slot__tunggu">{t('workspace.tiktok.cal.waitVideo')}</span>
            )}

            {/* Pemilih set duduk TERUS pada kad, bukan dalam dialog. Pengguna
                baru sahaja menjatuhkan produk di sini; membuka tetingkap lain
                semata-mata untuk menekan S2 memutuskan aliran yang sedang
                berjalan.

                ⭐ Set tanpa video TIDAK lagi dimatikan (keputusan user
                   2026-08-13). Dahulu ia dimatikan kerana slot hanya menerima
                   produk yang sudah bervideo; kini slot memang boleh menunggu,
                   jadi butang mati akan menghalang perkara yang dibenarkan.
                   Ia ditandakan sebagai "menunggu" sebaliknya — pengguna nampak
                   set mana yang videonya belum ada, tanpa disekat. */}
            <span className="s-slot__set">
                {['set1', 'set2', 'set3'].map((s, i) => {
                    const adaVideo = (isi.p.videos || [])[i] === 1;
                    /* Ditempah kalendar akaun lain. Ia TIDAK dimatikan —
                       mengulang video ke platform lain memang dibenarkan;
                       tandanya sahaja yang berubah supaya pengguna nampak
                       ia sudah wujud di tempat lain sebelum menekannya. */
                    const diLuar = adaVideo && isi.s !== s && !!luar[s];
                    return (
                        <button
                            key={s}
                            type="button"
                            aria-pressed={isi.s === s}
                            title={
                                !adaVideo ? t('workspace.tiktok.cal.setNoVideoWait', { n: i + 1 })
                                    : diLuar ? t('workspace.tiktok.cal.setOnOtherAccount', { n: i + 1, akaun: luar[s] })
                                        : t('workspace.tiktok.cal.useSet', { n: i + 1 })
                            }
                            className={
                                's-slot__pil'
                                + (adaVideo ? '' : ' s-slot__pil--tunggu')
                                + (diLuar ? ' s-slot__pil--luar' : '')
                                + (isi.s === s ? ' s-slot__pil--on' : '')
                                + ((statusGagal && isi.s === s) ? ' s-slot__pil--gagal' : '')
                            }
                            onClick={() => onTukarSet(hari, minit, s)}
                        >
                            S{i + 1}
                        </button>
                    );
                })}
            </span>

            {/* ★ LANGKAH 3 — label status SEBENAR, sentiasa kelihatan (bukan
                tooltip). Butang Retry hanya muncul pada slot GAGAL dan hanya
                bila pengendali Retry diberi (Langkah 4 — kini belum diwayar,
                jadi ia tidak muncul). */}
            {statusSlot && (
                <span className="s-slot__status">
                    {/* ★ [2026-09-27] Post now = "Posted", bukan "Scheduled" (md dicop semasa Run). */}
                    {statusSlot === 'complete' ? (isi.md === 'now' ? t('workspace.tiktok.cal.stPosted') : t('workspace.tiktok.cal.stDone'))
                        : statusSlot === 'draft' ? t('workspace.tiktok.cal.stDraft')
                            : statusSlot === 'failed' ? t('workspace.tiktok.cal.stFail')
                                : statusSlot === 'uncertain' ? t('workspace.tiktok.cal.stUncertain')
                                    : statusSlot === 'running' ? t('workspace.tiktok.cal.stRunning')
                                        : ''}
                </span>
            )}
            {/* ★ SEBAB — kenapa gagal / draf. Sentiasa kelihatan (bukan tooltip)
                supaya "Failed" tidak lagi kosong tanpa penjelasan. */}
            {statusSebab && (statusSlot === 'failed' || statusSlot === 'draft' || statusSlot === 'uncertain') && (
                <span className="s-slot__sebab">{statusSebab}</span>
            )}
            {statusGagal && onRetryGagal && (
                <button
                    type="button"
                    className="s-slot__retry"
                    title={t('workspace.tiktok.cal.retryTitle')}
                    onClick={() => onRetryGagal?.(hari, minit)}
                >
                    {t('workspace.tiktok.cal.retry')}
                </button>
            )}
            </div>

            {/* THUMBNAIL video set DIPILIH — kanan sekali, bersaiz sama seperti
                preview pada kad senarai kiri. Ia gambar PEGUN, bukan pemain video:
                tiada butang main, kerana video sebenar ditonton dari senarai kiri
                (arahan user 2026-08-20). Dua keadaan: ada poster → gambar · tiada
                poster (belum dijana / video local) → ikon video. Bingkai putus-
                putus menandakan set yang videonya belum dijana.
                `aria-hidden`: bantuan visual sahaja — nama, teks "menunggu video"
                dan status sudah membawa maklumat sama untuk pembaca skrin. */}
            <span
                className={'s-slot__gambar' + (menungguVideo ? ' s-slot__gambar--tunggu' : '')}
                aria-hidden="true"
            >
                {gambarSah ? (
                    <img
                        src={gambarSet} alt="" draggable={false}
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                    />
                ) : (
                    <Video size={16} strokeWidth={1.9} />
                )}
            </span>
        </div>
    );
}

/* ---------- Tambah waktu pada SATU hari -----------------------------
   Template ialah peraturan yang boleh diguna semula merentas hari; waktu
   di sini pula milik hari itu SAHAJA (keputusan user 2026-08-14). Sebab
   itu ia tidak menyentuh template langsung — hari lain yang memakai
   template yang sama tidak berubah walau sedikit, dan susunan hari itu
   tidak dikosongkan seperti yang berlaku apabila template ditukar. */
function TambahWaktu({ hari, sudahAda = [], onTambah }) {
    const t = useT();
    const [buka, setBuka] = useState(false);
    const [jam, setJam] = useState('09:00');
    const [ralat, setRalat] = useState('');

    const tutup = () => { setBuka(false); setRalat(''); };

    /* Dicadangkan pada saat pemilih dibuka, bukan sekali semasa komponen
       lahir — hari ini mungkin sudah menerima waktu tambahan sejak kali
       terakhir kotak ini dibuka. */
    const bukaPemilih = () => { setJam(cadangWaktuBaharu(sudahAda)); setRalat(''); setBuka(true); };

    /* Tiada pelarasan minit di sini lagi: pemilih tidak menawarkan minit
       di luar roda TikTok, jadi ia tidak boleh sampai ke sini. */
    const simpan = () => {
        const m = jamKeMinit(jam);
        /* Waktu berulang akan menghasilkan dua kotak pada jam yang sama dan
           satu daripadanya mustahil dibezakan daripada yang satu lagi. */
        if (sudahAda.includes(m)) { setRalat(t('workspace.tiktok.cal.timeExists')); return; }
        onTambah(hari, m);
        tutup();
    };

    if (!buka) {
        return (
            <button type="button" className="s-kal__tambah" onClick={bukaPemilih}>
                <Plus size={12} strokeWidth={2} aria-hidden="true" />
                {t('workspace.tiktok.cal.addTime')}
            </button>
        );
    }

    return (
        <div className="s-kal__tambahbox">
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <PilihWaktuV39
                    value={jam} label={t('workspace.tiktok.cal.newTimeLabel')} autoFocus tinggi={28}
                    onChange={(v) => { setJam(v); setRalat(''); }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') simpan();
                        if (e.key === 'Escape') tutup();
                    }}
                />
                <span style={{ flex: 1 }} />
                <button
                    type="button" className="s-iconbtn" onClick={simpan}
                    title={t('workspace.tiktok.save')} aria-label={t('workspace.tiktok.cal.saveTimeAria')}
                >
                    <Check size={14} strokeWidth={2} />
                </button>
                <button
                    type="button" className="s-iconbtn" onClick={tutup}
                    title={t('workspace.tiktok.cancel')} aria-label={t('workspace.tiktok.cancel')}
                >
                    <X size={14} strokeWidth={2} />
                </button>
            </div>
            {ralat && <span className="s-kal__ralat">{ralat}</span>}
        </div>
    );
}

/* ---------- Kalendar ------------------------------------------------ */

export default function KalendarMingguV39({
    templates = [], hariTemplate = {}, onPilihTemplate,
    isian = {}, onLetak, onLetakSet, onPindah, onPindahBanyak, onTukarSet, onBuang, capsyen,
    /* Tanda pilihan slot. Ia dipegang oleh pemanggil (JadualTiktokV39) dan
       bukan di sini kerana bar kalendar perlu melaporkan kiraannya, dan
       tindakan pukal (buang) hidup bersama isian di sana. */
    pilih = null, onPilih,
    /* Waktu tambahan pengguna, per hari: { [offsetHari]: [minit, …] }.
       Ia hidup pada hari itu sahaja dan TIDAK menyentuh template — lihat
       nota dalam `gabungWaktu`. */
    waktuTambahan = {}, onTambahWaktu, onBuangWaktu,
    /* Kotak tick pada kepala hari kini = "PILIH SEMUA slot hari itu" (arahan
       user 2026-08-20). Ia menyumbang ke pilihan slot yang sama (`pilih`), jadi
       ia muncul bila pemilihan tersedia (`onPilih` diberi) — tiada prop tanda
       tarikh berasingan lagi. */
    /* Set yang dipegang kalendar akaun lain — lihat nota penuh dalam
       JadualTiktokV39. Di sini ia hanya mengalir turun ke pil set. */
    setLuar = {},
    /* Set yang gagal dijadualkan, dikunci ikut slot: { [kunciSlot]: {sets, sebab} }.
       Kalendar hanya MEMAPAR — pemanggil yang memiliki data dan tindakan Retry,
       sama seperti `pilih`/`onPilih` di atas. */
    onRetryGagal,
    /* Langkah 5: kawalan per-slot dalam popup butiran — tukar status manual
       (`onTukarStatus(off,minit,st)`) + padam video (`onPadamVideo(pid,setKey)`).
       Tanpa prop, kawalan tidak muncul (prototaip). */
    onTukarStatus = null, onPadamVideo = null,
    /* BARIS 2 bar navigasi (arahan user 2026-08-21). Workspace sebenar
       mengisinya dengan penukar akaun + butang Schedule template (+ Undo);
       prototaip tidak menghantar apa-apa, jadi baris itu tidak dirender.
       Dulu ia diselit ke KANAN bar navigasi yang sama; kini bar itu
       bertingkat menegak dan barKiri jadi baris sendiri di bawah tarikh. */
    barKiri = null,
    /* Elemen DOM dalam JALUR TAB (schedMode) tempat bar tarikh (Today ·
       anak panah · julat) dipaparkan melalui portal — arahan user
       2026-08-21 "sebaris tab". Bila null (prototaip), bar tarikh kekal
       INLINE di bar navigasi kalendar seperti biasa. */
    slotNav = null,
    /* Slot KEDUA dalam jalur yang sama, ditolak ke hujung KANAN (arahan user
       26 Okt: "2 button tu bawa ke baris atas dan letak ke kanan sekali").
       Bila ada, `barKiri` (penukar akaun + Schedule template + Undo) diportal
       ke situ dan baris keduanya di sini hilang terus — satu baris penuh di
       atas grid hari dijimatkan. Tiada slot (prototaip / Classic) → barKiri
       kekal di bar navigasi seperti dahulu. */
    slotBarKiri = null,
    /* Jalur yang duduk TEPAT DI BAWAH bar navigasi, sebelum grid hari.
       Bar pukal slot dipasang di sini supaya lajur kalendar bermula dengan
       BARIS KEPALA seperti lajur senarai di sebelah kiri — kalau ia
       dirender sebelum bar navigasi, ia duduk lebih tinggi daripada bar
       pukal kad di kiri dan kedua-duanya nampak tidak sebaris. */
    barAtas = null,
    /* ⭐ v4 (suis generateV2, admin sahaja — arahan user 1 Nov): bar tarikh
       bertukar susunan ‹ · Today · › + "Bulan Tahun" besar (gaya diluluskan).
       Lalai false = laluan v3.9 live KEKAL byte-untuk-byte (Today · ‹ › · julat). */
    v4 = false,
}) {
    const t = useT();
    const [halaman, setHalaman] = useState(0);
    const [sasar, setSasar] = useState(null);
    const [buka, setBuka] = useState(null);

    /* Tetingkap hari dikira daripada TARIKH HARI INI setiap kali kalendar
       dilukis. `halaman` hanya menyatakan berapa tetingkap ke hadapan atau
       ke belakang daripada hari ini — jadi bila jam melepasi tengah malam,
       lajur bergerak dan isinya kekal pada tarikhnya sendiri. */
    const mula = tarikhTambah(tarikhHariIni(), halaman * HARI_SETINGKAP);
    const hari = Array.from({ length: HARI_SETINGKAP }, (_, i) => tarikhTambah(mula, i));

    /* ---------- Tanda pilihan slot ------------------------------------ */
    const pilihanAktif = pilih instanceof Set ? pilih : null;
    const bolehPilih = !!(pilihanAktif && onPilih);
    const kunciTerakhir = useRef(null);
    const gridRef = useRef(null);
    const [kotak, setKotak] = useState(null);   // marquee: {x1,y1,x2,y2}

    /* Turutan slot SEPERTI YANG DILIHAT — hari dahulu, kemudian waktu.
       Shift+klik memilih julat mengikut turutan ini, bukan mengikut susunan
       kunci dalam objek (yang tiada makna kepada mata pengguna). */
    const turutanKelihatan = () => {
        const senarai = [];
        hari.forEach((off) => {
            muatanHari(off).forEach((m) => {
                const k = kunciSlot(off, m);
                if (isian[k]) senarai.push(k);
            });
        });
        return senarai;
    };

    const togglePilih = (kunci, shift) => {
        if (!bolehPilih) return;
        const baru = new Set(pilihanAktif);

        if (shift && kunciTerakhir.current) {
            const senarai = turutanKelihatan();
            const a = senarai.indexOf(kunciTerakhir.current);
            const b = senarai.indexOf(kunci);
            if (a !== -1 && b !== -1) {
                const [i, j] = a < b ? [a, b] : [b, a];
                for (let n = i; n <= j; n++) baru.add(senarai[n]);
                onPilih(baru);
                return;
            }
        }

        if (baru.has(kunci)) baru.delete(kunci);
        else baru.add(kunci);
        kunciTerakhir.current = kunci;
        onPilih(baru);
    };

    /* ★ Kotak tick kepala hari = PILIH SEMUA slot berisi hari itu (arahan user
       2026-08-20). Ia menyumbang ke pilihan yang SAMA seperti seret/klik slot,
       jadi bar pukal + butang Run membacanya serentak. Klik bila belum semua
       terpilih → tambah semua slot hari itu; klik bila sudah semua → nyahpilih
       semua. `muatanHari` ditakrif lebih bawah tetapi dipanggil hanya semasa
       klik, jadi ia sudah dalam skop. */
    const kunciHariBerisi = (off) => muatanHari(off)
        .map((m) => kunciSlot(off, m))
        .filter((k) => isian[k]);
    const semuaHariDipilih = (off) => {
        const k = kunciHariBerisi(off);
        return k.length > 0 && k.every((x) => pilihanAktif?.has(x));
    };
    const pilihHari = (off) => {
        if (!bolehPilih) return;
        const kunci = kunciHariBerisi(off);
        if (!kunci.length) return;
        const baru = new Set(pilihanAktif);
        if (semuaHariDipilih(off)) kunci.forEach((k) => baru.delete(k));
        else kunci.forEach((k) => baru.add(k));
        onPilih(baru);
    };

    /* ---------- Kotak pilihan (marquee) -------------------------------
       Bermula HANYA pada ruang kosong: menekan slot berisi ialah permulaan
       seretan pindah, dan dua gerak isyarat yang bermula di tempat sama
       akan bertelagah. Corak sama seperti senarai kad di halaman workspace,
       jadi gerak isyarat yang pengguna sudah tahu terpakai di sini juga. */
    const mulaKotak = (e) => {
        if (!bolehPilih || e.button !== 0) return;
        /* Menu PilihV39 dirender melalui portal di luar grid, tetapi event
           React tetap menggelegak ke sini — tanpa semakan DOM ini, memilih
           template akan dikira "klik ruang kosong" dan menyahpilih slot. */
        if (!gridRef.current?.contains(e.target)) return;
        if (e.target.closest('.s-slot--isi, button, select, input, .s-kal__tambahbox')) return;
        const r = gridRef.current?.getBoundingClientRect();
        if (!r) return;
        setKotak({ x1: e.clientX - r.left, y1: e.clientY - r.top, x2: e.clientX - r.left, y2: e.clientY - r.top });
    };

    const gerakKotak = (e) => {
        if (!kotak) return;
        const r = gridRef.current.getBoundingClientRect();
        setKotak((k) => ({ ...k, x2: e.clientX - r.left, y2: e.clientY - r.top }));
    };

    const habisKotak = (e) => {
        if (!kotak) return;
        const luas = Math.abs(kotak.x2 - kotak.x1) + Math.abs(kotak.y2 - kotak.y1);
        setKotak(null);
        if (luas < 8) {
            /* Klik biasa pada ruang kosong grid = nyahpilih semua (arahan user
               2026-09-24, skop: kawasan kalendar sahaja). Hanya pada mouseup —
               tetikus keluar grid semasa ditekan bukan klik. Klik slot berisi
               kekal menambah/membuang tanda seperti biasa (tidak sampai ke sini). */
            if (e?.type === 'mouseup' && pilihanAktif.size > 0) {
                kunciTerakhir.current = null;
                onPilih(new Set());
            }
            return;
        }

        const r = gridRef.current.getBoundingClientRect();
        const kiri = Math.min(kotak.x1, kotak.x2), kanan = Math.max(kotak.x1, kotak.x2);
        const atas = Math.min(kotak.y1, kotak.y2), bawah = Math.max(kotak.y1, kotak.y2);

        const baru = new Set(pilihanAktif);
        gridRef.current.querySelectorAll('.s-slot--isi[data-kunci-slot]').forEach((el) => {
            const b = el.getBoundingClientRect();
            const x1 = b.left - r.left, x2 = b.right - r.left;
            const y1 = b.top - r.top, y2 = b.bottom - r.top;
            if (x2 >= kiri && x1 <= kanan && y2 >= atas && y1 <= bawah) baru.add(el.dataset.kunciSlot);
        });
        onPilih(baru);
    };

    const label = (off) => {
        const d = tarikhKeDate(off);
        return `${d.getDate()} ${BULAN_LABEL[d.getMonth()]}`;
    };
    const tahun = tarikhKeDate(hari[hari.length - 1]).getFullYear();

    /* Parameter dinamakan `tpl`, bukan `t` — `t` di dalam fail ini ialah
       fungsi terjemahan, dan nama yang sama akan menutupnya secara senyap. */
    const templateHari = (off) => templates.find((tpl) => tpl.id === hariTemplate[off]);

    /* ⭐ Waktu yang BERISI pada setiap hari — digabung ke dalam senarai papar
       supaya slot yang sudah diletak KEKAL kelihatan walaupun waktunya sudah
       TIADA lagi dalam template (template diubah selepas video diletak —
       cth Fill semula dengan selang lain, atau chip waktu dibuang). Tanpa
       gabungan ini entri itu jadi "slot hantu": wujud dalam simpanan, tidak
       kelihatan di kalendar, tidak dikira dalam angka terisi, tidak boleh
       ditick — dan titik set pada kad kekal bertanda "sudah dijadualkan",
       jadi seret semula ditolak tanpa apa-apa kelihatan (siasatan
       2026-08-26). PADAM template tidak bergantung pada laluan ini — hari
       yang memakainya terus dikosongkan oleh pemanggil (lihat
       `padamTemplateDariKalendar` dalam WorkspaceView).
       Dikira terus setiap render TANPA useMemo — senarai isian kecil, gaya
       sama seperti `turutanKelihatan`/`muatanHari` di atas, dan memo manual
       di sini ditolak oleh lint React Compiler. */
    const waktuIsi = (() => {
        const peta = {};
        for (const k in isian) {
            if (!isian[k]) continue;
            /* Kunci = `${tarikh}_${minit}`; tarikh membawa sempang, jadi
               pecah dari HUJUNG — corak sama seperti handleRunTikTokKalendar. */
            const pisah = String(k).split('_');
            const minit = Number(pisah.pop());
            const off = pisah.join('_');
            if (!adalahTarikh(off) || !Number.isFinite(minit)) continue;
            (peta[off] = peta[off] || []).push(minit);
        }
        return peta;
    })();

    /* Waktu "tambahan" untuk PAPARAN satu hari = waktu yang pengguna taip
       sendiri + waktu yang berisi. */
    const tambahanPapar = (off) => gabungWaktu(waktuTambahan[off] || [], waktuIsi[off] || []);

    /* Keupayaan SEBENAR hari itu mengikut templatenya (cth 08:00–23:00 setiap
       30 minit = 31): waktu template penuh + waktu tambahan + waktu berisi.
       Kepala hari melaporkan angka ini. */
    const muatanHari = (off) => gabungWaktu(slotHari(templateHari(off), HAD_SLOT_SEHARI), tambahanPapar(off));

    /* Kotak yang DIPAPARKAN = senarai PENUH yang sama (arahan user
       2026-08-26). Dulu paparan bermula 10 kotak dan "tumbuh" bila terisi
       (`slotHariPapar`, kini dipadam) — pelanggan yang sengaja membina
       template 31 slot untuk seret banyak kad membacanya sebagai slot
       hilang: sasaran seretan 13:00+ memang tiada di skrin. ⛔ Jangan
       kembalikan had paparan tanpa arahan user. */
    const slotUntuk = muatanHari;

    /* ⭐ Bar tarikh (Today · anak panah · julat tarikh) — arahan user
       2026-08-21 ia boleh duduk di JALUR TAB (via `slotNav`, portal) atau
       INLINE di bar navigasi kalendar (prototaip). Ditakrif sekali di sini,
       dipakai di dua tempat supaya tiada salinan JSX yang boleh terpesong. */
    const btnPrev = (
        <button
            type="button" className="s-iconbtn"
            aria-label={t('workspace.tiktok.cal.prevDays', { n: HARI_SETINGKAP })}
            title={t('workspace.tiktok.cal.prevDays', { n: HARI_SETINGKAP })}
            onClick={() => setHalaman((v) => v - 1)}
        >
            <ChevronLeft size={15} strokeWidth={2} />
        </button>
    );
    const btnNext = (
        <button
            type="button" className="s-iconbtn"
            aria-label={t('workspace.tiktok.cal.nextDays', { n: HARI_SETINGKAP })}
            title={t('workspace.tiktok.cal.nextDays', { n: HARI_SETINGKAP })}
            onClick={() => setHalaman((v) => v + 1)}
        >
            <ChevronRight size={15} strokeWidth={2} />
        </button>
    );
    const btnToday = (
        <Btn variant="ghost" size="sm" onClick={() => setHalaman(0)}>{t('workspace.tiktok.cal.today')}</Btn>
    );
    /* v4: "Bulan Tahun" penuh (ikut hari PERTAMA tetingkap). BULAN_PENUH =
       Inggeris sama macam BULAN_LABEL (kalendar ini memang label Inggeris). */
    const bulanTahunV4 = (() => {
        const d = tarikhKeDate(hari[0]);
        return `${BULAN_PENUH[d.getMonth()]} ${d.getFullYear()}`;
    })();
    /* ⭐ v4 (arahan user 1 Nov): ‹ · Today · › + "Bulan Tahun" besar.
       v3.9 (v4=false): Today · ‹ › + julat tarikh — susunan asal, tak berubah. */
    const barTarikh = v4 ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {btnPrev}
            {btnToday}
            {btnNext}
            <span style={{ fontSize: 20, fontWeight: 600, color: 'var(--s-ink)', marginLeft: 4 }}>
                {bulanTahunV4}
            </span>
        </span>
    ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {btnToday}
            <span style={{ display: 'inline-flex', gap: 2 }}>
                {btnPrev}
                {btnNext}
            </span>
            <span style={{ fontSize: 13, color: 'var(--s-ink-2)' }}>
                {label(hari[0])} – {label(hari[hari.length - 1])} {tahun}
            </span>
        </span>
    );

    return (
        /* `s-kal__akar` dipakai HANYA oleh tab Schedule (lihat `.s-tikjad__kanan`
           dalam ui.css) untuk mengekalkan baris akaun di tempatnya sementara grid
           hari menatal. Di luar konteks itu kelas ini tidak membawa sebarang gaya. */
        <div className="s-kal__akar">
            {/* ---- Bar navigasi ---- */}
            {/* Kedua-dua baris sudah naik ke jalur alat → bar navigasi di sini
                TIDAK dirender langsung; kalau dibiar, jurang 14px tertinggal
                menganga di atas grid hari. */}
            {!(slotNav && slotBarKiri) && (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 10, marginBottom: 14,
            }}>
                {/* ⭐ Bar tarikh: bila `slotNav` ada (schedMode) ia DIPINDAH ke
                    jalur tab melalui portal (arahan user 2026-08-21 "sebaris
                    tab") dan TIDAK dirender di sini; bila tiada (prototaip), ia
                    kekal inline sebagai baris pertama. `barKiri` (akaun +
                    template + Undo + Show Control Panel) kekal di bar navigasi.
                    Tajuk "Calendar" + kiraan keseluruhan kekal DIBUANG (arahan
                    user 2026-08-10) — kiraan berguna ada pada setiap kepala hari. */}
                {!slotNav && barTarikh}
                {/* Baris akaun — penukar akaun + Schedule template (+ Undo) di
                    kiri, Show Control Panel ditolak ke hujung kanan (dalam barKiri). */}
                {!slotBarKiri && barKiri}
            </div>
            )}
            {/* Portal bar tarikh ke jalur tab bila schedMode menyediakan slotnya. */}
            {slotNav && createPortal(barTarikh, slotNav)}
            {/* Penukar akaun + Schedule template di hujung KANAN baris tarikh.
                ⚠️ Pemilih akaun TIDAK boleh hilang semasa menatal (arahan user
                   2026-08-23) — jalur alat memang kekal di tempatnya, jadi
                   peraturan itu masih dipatuhi selepas pindah ini. */}
            {slotBarKiri && barKiri && createPortal(barKiri, slotBarKiri)}

            {barAtas}

            {/* Baris "Times shown in Asia/Kuala_Lumpur" DIBUANG (arahan user
                2026-08-20) — semua pengguna di zon KL, jadi ia hanya baris
                tambahan. Zon waktu masih tertera dalam modal Schedule template. */}

            {/* ---- Grid hari ----
                Bilangan lajur ditetapkan di sini, bukan dalam CSS, supaya
                ia dan langkah butang ‹ › membaca pemalar yang SAMA. Dua
                nombor berasingan bermakna butang boleh melangkau atau
                mengulang hari tanpa sesiapa perasan. */}
            <div className="s-kal__skrol">
                <div
                    ref={gridRef}
                    className="s-kal__grid"
                    style={{ gridTemplateColumns: `repeat(${HARI_SETINGKAP}, minmax(0, 1fr))`, position: 'relative' }}
                    onMouseDown={mulaKotak}
                    onMouseMove={gerakKotak}
                    onMouseUp={habisKotak}
                    onMouseLeave={habisKotak}
                >
                    {/* Kotak pilihan. `pointerEvents: none` wajib — tanpanya kotak
                        itu sendiri menjadi sasaran tetikus dan slot di bawahnya
                        tidak pernah menerima seretan. */}
                    {kotak && (
                        <div style={{
                            position: 'absolute',
                            left: Math.min(kotak.x1, kotak.x2),
                            top: Math.min(kotak.y1, kotak.y2),
                            width: Math.abs(kotak.x2 - kotak.x1),
                            height: Math.abs(kotak.y2 - kotak.y1),
                            border: '1px solid var(--s-select, #2563eb)',
                            background: 'color-mix(in srgb, var(--s-select, #2563eb) 12%, transparent)',
                            borderRadius: 4, pointerEvents: 'none', zIndex: 20,
                        }} />
                    )}
                    {hari.map((off) => {
                        const d = tarikhKeDate(off);
                        const idTpl = hariTemplate[off] || '';
                        const slot = slotUntuk(off);
                        const muatan = muatanHari(off);
                        const terisi = muatan.filter((m) => isian[kunciSlot(off, m)]).length;
                        const hariIni = bezaHariIni(off) === 0;

                        return (
                            <div key={off} className="s-kal__lajur">
                                {/* "Today · Aug 10" — bukan nombor besar. Pengguna
                                    menjadualkan kerja beberapa hari ke hadapan, jadi
                                    soalan pertamanya ialah "hari ini atau esok?",
                                    bukan "haribulan berapa?". Tarikh sebenar kekal
                                    di sebelahnya untuk yang perlukannya. */}
                                <div className={'s-kal__hari' + (hariIni ? ' s-kal__hari--kini' : '')}>
                                    {/* ★ Kotak tick = PILIH SEMUA slot hari itu (arahan user
                                        2026-08-20). Muncul bila pemilihan tersedia
                                        (`bolehPilih`) dan hari itu ADA slot berisi — hari
                                        kosong tiada apa untuk dipilih. */}
                                    {bolehPilih && kunciHariBerisi(off).length > 0 && (
                                        <input
                                            type="checkbox"
                                            className="s-kal__tick"
                                            checked={semuaHariDipilih(off)}
                                            onChange={() => pilihHari(off)}
                                            aria-label={t('workspace.tiktok.cal.tickDay', { hari: HARI_PENUH[d.getDay()], tarikh: d.getDate() })}
                                            title={t('workspace.tiktok.cal.pilihHariTip')}
                                        />
                                    )}
                                    <span className="s-kal__tajukhari">{namaHari(off, d, t)}</span>
                                    <span className="s-kal__tarikh">
                                        {BULAN_LABEL[d.getMonth()]} {d.getDate()}
                                    </span>
                                    <span className="s-kal__kira">
                                        {muatan.length === 0 ? '—' : `${terisi}/${muatan.length}`}
                                    </span>
                                </div>

                                {/* Template dipilih PER HARI. Ia diletak di bawah
                                    tarikh, bukan di atas kalendar, supaya jelas ia
                                    hanya mengubah hari ini sahaja. */}
                                <div style={{ marginBottom: 8 }}>
                                    <PilihV39
                                        ariaLabel={t('workspace.tiktok.cal.templateForAria', {
                                            hari: HARI_PENUH[d.getDay()], tarikh: d.getDate(),
                                        })}
                                        nilai={idTpl}
                                        onPilih={(id) => onPilihTemplate(off, id)}
                                        pilihan={[
                                            { id: '', label: t('workspace.tiktok.cal.noTemplate') },
                                            ...templates.map((tpl) => ({ id: tpl.id, label: tpl.nama })),
                                        ]}
                                        tinggi={28}
                                        saizTeks={11.5}
                                        lebarMenu={220}
                                    />
                                </div>

                                <div className="s-kal__isi">
                                    {/* DUA kunci penuh, bukan satu ayat yang dicantum
                                        daripada serpihan: bahasa lain tidak semestinya
                                        meletakkan klausa tambahan itu di hujung. */}
                                    {slot.length === 0 ? (
                                        <p className="s-kal__pilih">
                                            {onTambahWaktu
                                                ? t('workspace.tiktok.cal.pickTemplateOrAdd')
                                                : t('workspace.tiktok.cal.pickTemplate')}
                                        </p>
                                    ) : slot.map((m) => (
                                        <Slot
                                            key={m}
                                            hari={off}
                                            minit={m}
                                            isi={isian[kunciSlot(off, m)] || null}
                                            luar={setLuar[isian[kunciSlot(off, m)]?.p?.id]}
                                            onRetryGagal={onRetryGagal}
                                            sasar={sasar}
                                            setSasar={setSasar}
                                            onLetak={onLetak}
                                            onLetakSet={onLetakSet}
                                            onPindah={onPindah}
                                            onPindahBanyak={onPindahBanyak}
                                            dipilih={!!pilihanAktif?.has(kunciSlot(off, m))}
                                            pilihanSemua={pilihanAktif}
                                            onToggle={bolehPilih ? togglePilih : undefined}
                                            onTukarSet={onTukarSet}
                                            onBuang={onBuang}
                                            onButiran={setBuka}
                                            /* Waktu yang pengguna tambah sendiri boleh dibuang
                                               semula SELAGI ia kosong. Waktu template tidak —
                                               itu milik template, dan membuangnya di sini akan
                                               menjadikan hari ini berbeza daripada peraturannya
                                               tanpa sebarang jejak. */
                                            waktuSendiri={(waktuTambahan[off] || []).includes(m)}
                                            onBuangWaktu={onBuangWaktu}
                                        />
                                    ))}

                                    {onTambahWaktu && (
                                        <TambahWaktu
                                            hari={off}
                                            sudahAda={muatan}
                                            onTambah={onTambahWaktu}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {buka && (
                <ButiranModal
                    isi={buka}
                    namaTemplate={templates.find((tpl) => tpl.id === hariTemplate[buka.hari])?.nama}
                    capsyen={capsyen}
                    onClose={() => setBuka(null)}
                    /* Status LANGSUNG dibaca daripada isian ikut kunci slot,
                       bukan daripada snapshot `buka` (yang beku pada saat popup
                       dibuka) — supaya dropdown menunjuk nilai terkini dan
                       berubah serta-merta selepas ditukar. */
                    stSekarang={isian[kunciSlot(buka.hari, buka.minit)]?.st || ''}
                    sbSekarang={isian[kunciSlot(buka.hari, buka.minit)]?.sb || ''}
                    mdSekarang={isian[kunciSlot(buka.hari, buka.minit)]?.md || ''}
                    onTukarStatus={onTukarStatus ? (st) => onTukarStatus(buka.hari, buka.minit, st) : null}
                    onPadamVideo={onPadamVideo ? () => onPadamVideo(buka.pid || buka.p?.id, buka.s) : null}
                />
            )}
        </div>
    );
}
